import createClient from 'openapi-fetch'
import { API_BASE_URL } from './client'
import type { components, paths } from './schema'

type PublicShareDocumentRead = components['schemas']['PublicShareDocumentRead']
type PublicShareFieldPatch = components['schemas']['PublicShareFieldPatch']
type PublicShareFieldRead = components['schemas']['FieldRead']
type PublicShareCommentCreate = components['schemas']['PublicShareCommentCreate']
type DocumentAccessEventRead = components['schemas']['DocumentAccessEventRead']

const publicShareClient = createClient<paths>({
  baseUrl: API_BASE_URL,
  fetch: (request: Request) => fetch(request, { credentials: 'omit' }),
})

export async function fetchPublicShare(
  shareToken: string,
): Promise<PublicShareDocumentRead> {
  return fetchPublicShareData(shareToken)
}

export async function fetchPublicShareData(
  shareToken: string,
): Promise<PublicShareDocumentRead> {
  const { data, error } = await publicShareClient.GET('/share/{token}/data', {
    params: { path: { token: shareToken } },
  })
  if (!data) {
    throw new Error(publicShareErrorMessage(error, 'Public share unavailable'))
  }
  return data
}

export async function patchPublicShareField(
  shareToken: string,
  fieldUuid: string,
  body: PublicShareFieldPatch,
): Promise<PublicShareFieldRead> {
  const { data, error } = await publicShareClient.PATCH(
    '/share/{token}/fields/{field_uuid}',
    {
      params: { path: { token: shareToken, field_uuid: fieldUuid } },
      body,
    },
  )
  if (!data) {
    throw new Error(publicShareErrorMessage(error, 'Public share field update failed'))
  }
  return data
}

export async function createPublicShareComment(
  shareToken: string,
  body: PublicShareCommentCreate,
): Promise<DocumentAccessEventRead> {
  const { data, error } = await publicShareClient.POST('/share/{token}/comments', {
    params: { path: { token: shareToken } },
    body,
  })
  if (!data) {
    throw new Error(publicShareErrorMessage(error, 'Public share comment failed'))
  }
  return data
}

function publicShareErrorMessage(error: unknown, fallback: string): string {
  const record =
    error !== null && typeof error === 'object'
      ? error as Record<string, unknown>
      : null
  const detail = record ? record.detail : null
  return typeof detail === 'string' && detail.trim() ? detail : fallback
}
